import api from './client';
import type { Dossier } from 'types/dossier';
import type { ListParams } from './types';

export interface DocumentClassification {
  type_key: string;
  type_label: string;
  confidence: number;
  suggested_name: string;
}

export const dossiersApi = {
  list: async (params?: ListParams & { org_id?: string; type?: string; offset?: number; sort_by?: string; sort_dir?: string }): Promise<{ items: Dossier[]; total: number }> => {
    const resp = await api.get('/dossiers', { params });
    return { items: resp.data, total: parseInt(resp.headers['x-total-count'] || '0', 10) };
  },

  get: async (id: string): Promise<Dossier> => {
    const { data } = await api.get(`/dossiers/${id}`);
    return data;
  },

  create: async (dossier: Omit<Dossier, 'id'>): Promise<Dossier> => {
    const { data } = await api.post('/dossiers', dossier);
    return data;
  },

  update: async (id: string, updates: Partial<Dossier>): Promise<Dossier> => {
    const { data } = await api.patch(`/dossiers/${id}`, updates);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/dossiers/${id}`);
  },

  uploadDocument: async (dossierId: string, file: File, name?: string, documentType?: string): Promise<Dossier> => {
    const formData = new FormData();
    formData.append('file', file);
    if (name) formData.append('name', name);
    if (documentType) formData.append('document_type', documentType);
    const { data } = await api.post(`/dossiers/${dossierId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  importDocumentFromGcs: async (dossierId: string, sourceGcsPath: string, name?: string, documentType?: string): Promise<Dossier> => {
    const { data } = await api.post(`/dossiers/${dossierId}/documents/import-from-gcs`, {
      source_gcs_path: sourceGcsPath,
      name,
      document_type: documentType,
    });
    return data;
  },

  getDocumentUrl: async (dossierId: string, documentId: string): Promise<string> => {
    const { data } = await api.get(`/dossiers/${dossierId}/documents/${documentId}/url`);
    return data.url;
  },

  deleteDocument: async (dossierId: string, documentId: string): Promise<Dossier> => {
    const { data } = await api.delete(`/dossiers/${dossierId}/documents/${documentId}`);
    return data;
  },

  addNote: async (dossierId: string, text: string): Promise<Dossier> => {
    const { data } = await api.post(`/dossiers/${dossierId}/notes`, { text });
    return data;
  },

  updateNote: async (dossierId: string, noteId: string, text: string): Promise<Dossier> => {
    const { data } = await api.patch(`/dossiers/${dossierId}/notes/${noteId}`, { text });
    return data;
  },

  deleteNote: async (dossierId: string, noteId: string): Promise<Dossier> => {
    const { data } = await api.delete(`/dossiers/${dossierId}/notes/${noteId}`);
    return data;
  },

  transferVehicle: async (dossierId: string, vehicleId: string, targetDossierId: string): Promise<Dossier> => {
    const { data } = await api.post(`/dossiers/${dossierId}/vehicles/${vehicleId}/transfer`, {
      target_dossier_id: targetDossierId,
    });
    return data;
  },

  classifyDocument: async (file: File): Promise<DocumentClassification> => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post('/dossiers/documents/classify', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
};
